import React, { useState, useRef, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';

const COUNTRY_CODES = [
  { code: '+44', country: 'GB', label: '🇬🇧 +44' },
  { code: '+1',  country: 'US', label: '🇺🇸 +1' },
  { code: '+353', country: 'IE', label: '🇮🇪 +353' },
  { code: '+33',  country: 'FR', label: '🇫🇷 +33' },
  { code: '+49',  country: 'DE', label: '🇩🇪 +49' },
  { code: '+34',  country: 'ES', label: '🇪🇸 +34' },
  { code: '+39',  country: 'IT', label: '🇮🇹 +39' },
  { code: '+31',  country: 'NL', label: '🇳🇱 +31' },
  { code: '+61',  country: 'AU', label: '🇦🇺 +61' },
  { code: '+91',  country: 'IN', label: '🇮🇳 +91' },
  { code: '+1',   country: 'CA', label: '🇨🇦 +1' },
];

const OTP_COOLDOWN = 60;

function parseInitial(val) {
  if (!val) return { dialCode: '+44', local: '' };
  for (const c of COUNTRY_CODES) {
    if (val?.startsWith(c?.code)) {
      return { dialCode: c?.code, local: val?.slice(c?.code?.length)?.trim() };
    }
  }
  return { dialCode: '+44', local: val };
}

export default function ProfilePhoneField({ value, onChange, error, onVerified, verifiedPhone, originalPhone }) {
  const parsed = parseInitial(value);
  const [dialCode, setDialCode] = useState(parsed?.dialCode);
  const [localNumber, setLocalNumber] = useState(parsed?.local);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef(null);

  const [otpStep, setOtpStep] = useState('idle');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  function getInternational(local, dial) {
    const stripped = local?.replace(/\s/g, '')?.replace(/^0+/, '');
    return stripped ? `${dial}${stripped}` : '';
  }

  const internationalPhone = getInternational(localNumber, dialCode);
  const isUnchanged = internationalPhone === originalPhone;
  const isVerified = isUnchanged || (verifiedPhone && verifiedPhone === internationalPhone);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => { if (cooldownRef?.current) clearInterval(cooldownRef?.current); };
  }, []);

  function startCooldown() {
    setCooldown(OTP_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownRef?.current); return 0; }
        return c - 1;
      });
    }, 1000);
  }

  function handleLocalChange(e) {
    const raw = e?.target?.value?.replace(/[^\d\s\-().+]/g, '');
    setLocalNumber(raw);
    const international = getInternational(raw, dialCode);
    onChange({ target: { name: 'mobile', value: international } });
    if (otpStep === 'verified' || otpStep === 'sent') {
      setOtpStep('idle');
      setOtpCode('');
      setOtpError('');
      onVerified('');
    }
  }

  function handleDialCodeSelect(code) {
    setDialCode(code);
    setDropdownOpen(false);
    const international = getInternational(localNumber, code);
    onChange({ target: { name: 'mobile', value: international } });
    if (otpStep === 'verified' || otpStep === 'sent') {
      setOtpStep('idle');
      setOtpCode('');
      setOtpError('');
      onVerified('');
    }
  }

  async function handleSendOtp() {
    if (!internationalPhone || internationalPhone?.replace(/\D/g, '')?.length < 7) {
      setOtpError('Please enter a valid phone number first.');
      return;
    }
    setOtpStep('sending');
    setOtpError('');
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke('send-sms-otp', {
        body: { phone: internationalPhone, action: 'send' }
      });
      if (fnError || data?.error) {
        setOtpError(fnError?.message || data?.error || 'Failed to send code. Please try again.');
        setOtpStep('error');
      } else {
        setOtpStep('sent');
        startCooldown();
      }
    } catch (err) {
      setOtpError('Network error. Please try again.');
      setOtpStep('error');
    }
  }

  async function handleVerifyOtp() {
    if (!otpCode || otpCode?.length !== 6) {
      setOtpError('Please enter the 6-digit code.');
      return;
    }
    setOtpStep('verifying');
    setOtpError('');
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke('send-sms-otp', {
        body: { phone: internationalPhone, action: 'verify', otp: otpCode }
      });
      if (fnError || data?.error) {
        setOtpError(fnError?.message || data?.error || 'Invalid code. Please try again.');
        setOtpStep('sent');
      } else if (data?.verified) {
        setOtpStep('verified');
        setOtpError('');
        onVerified(internationalPhone);
      }
    } catch (err) {
      setOtpError('Network error. Please try again.');
      setOtpStep('sent');
    }
  }

  const selectedEntry = COUNTRY_CODES?.find((c) => c?.code === dialCode) || COUNTRY_CODES?.[0];

  return (
    <div>
      <label htmlFor="mobile" className="block text-sm font-medium text-foreground mb-1">
        Mobile Number
        <span className="text-destructive ml-1">*</span>
        {isVerified && (
          <span className="ml-2 inline-flex items-center gap-1 text-xs text-success font-normal">
            <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
            {isUnchanged ? 'Current number' : 'Verified'}
          </span>
        )}
      </label>
      <div ref={containerRef} className="flex gap-0 relative">
        <button
          type="button"
          onClick={() => setDropdownOpen((o) => !o)}
          disabled={isVerified && !isUnchanged}
          className={`flex items-center gap-1 px-3 py-2 border rounded-l-md bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors whitespace-nowrap disabled:opacity-60 ${
            error ? 'border-destructive' : isVerified ? 'border-success' : 'border-input'
          }`}
          aria-haspopup="listbox"
          aria-expanded={dropdownOpen}
        >
          <span>{selectedEntry?.label}</span>
          <Icon name="ChevronDown" size={14} />
        </button>
        <input
          id="mobile"
          name="mobile"
          type="tel"
          placeholder="7911 123456"
          value={localNumber}
          onChange={handleLocalChange}
          disabled={isVerified && !isUnchanged}
          maxLength={15}
          className={`flex-1 px-3 py-2 border-t border-b border-r rounded-r-md bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors disabled:opacity-60 ${
            error ? 'border-destructive focus:ring-destructive' : isVerified ? 'border-success' : 'border-input'
          }`}
        />
        {dropdownOpen && (
          <ul
            role="listbox"
            className="absolute z-50 top-full left-0 mt-1 w-48 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {COUNTRY_CODES?.map((c) => (
              <li
                key={`${c?.code}-${c?.country}`}
                role="option"
                aria-selected={c?.code === dialCode}
                onClick={() => handleDialCodeSelect(c?.code)}
                className={`px-3 py-2 text-sm cursor-pointer ${
                  c?.code === dialCode ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                }`}
              >
                {c?.label} {c?.country}
              </li>
            ))}
          </ul>
        )}
      </div>
      {!isUnchanged && !isVerified && (
        <p className="mt-1 text-xs text-amber-600 flex items-center gap-1">
          <Icon name="AlertTriangle" size={12} />
          New number must be verified before saving
        </p>
      )}
      {!isVerified && !isUnchanged && (otpStep === 'idle' || otpStep === 'error') && (
        <div className="mt-2">
          <button
            type="button"
            onClick={handleSendOtp}
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-medium disabled:opacity-50"
          >
            <Icon name="MessageSquare" size={14} />
            Send verification code
          </button>
          {otpError && (
            <p className="mt-1 text-xs text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {otpError}
            </p>
          )}
        </div>
      )}
      {otpStep === 'sending' && (
        <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
          <Icon name="Loader" size={12} />
          Sending code to {internationalPhone}…
        </p>
      )}
      {(otpStep === 'sent' || otpStep === 'verifying') && (
        <div className="mt-3 p-3 bg-muted/40 rounded-lg border border-border">
          <p className="text-xs text-muted-foreground mb-2">
            A 6-digit code was sent to <span className="font-medium text-foreground">{internationalPhone}</span>. Enter it below:
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={otpCode}
              onChange={(e) => {
                setOtpCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6));
                setOtpError('');
              }}
              className="w-32 px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground text-center tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={otpStep === 'verifying' || otpCode?.length !== 6}
              className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {otpStep === 'verifying' ? 'Verifying…' : 'Verify'}
            </button>
          </div>
          {otpError && (
            <p className="mt-1.5 text-xs text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {otpError}
            </p>
          )}
          <div className="mt-2">
            {cooldown > 0 ? (
              <p className="text-xs text-muted-foreground">Resend in {cooldown}s</p>
            ) : (
              <button type="button" onClick={handleSendOtp} className="text-xs text-primary hover:underline">
                Resend code
              </button>
            )}
          </div>
        </div>
      )}
      {isVerified && !isUnchanged && (
        <div className="mt-2 flex items-center justify-between">
          <p className="text-xs text-success flex items-center gap-1">
            <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
            Phone number verified successfully
          </p>
          <button
            type="button"
            onClick={() => {
              setOtpStep('idle');
              setOtpCode('');
              setOtpError('');
              onVerified('');
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Change number
          </button>
        </div>
      )}
      {error && (
        <p className="mt-1 text-xs text-destructive flex items-center gap-1">
          <Icon name="AlertCircle" size={12} />
          {error}
        </p>
      )}
    </div>
  );
}
